import React, { useState, useRef, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Spinner } from "../ui/spinner";
import { useDirtyState } from "../../hooks/useDirtyState";
import { DirtyStateDialog } from "../shared/DirtyStateDialog";

const EditStock = ({ editStock, setEditStock, handleUpdate }) => {
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { markDirty, resetDirty, showDialog, confirmNavigation, cancelNavigation } = useDirtyState(editStock);
    const prevEditStockRef = useRef(editStock);

    useEffect(() => {
        if (prevEditStockRef.current !== editStock && editStock) {
            resetDirty(editStock);
        }
        prevEditStockRef.current = editStock;
    }, [editStock, resetDirty]);

    const validate = (data) => {
        const errs = {};
        if (!data.company_name || data.company_name.trim() === '') errs.company_name = 'Ce champ est requis';
        else if (data.company_name.length > 255) errs.company_name = 'Maximum 255 caractères';
        return errs;
    };

    const handleInputChange = (field, value) => {
        setEditStock(prev => {
            const updated = { ...prev, [field]: value };
            markDirty(updated);
            return updated;
        });
    };

    const handleSubmit = async () => {
        const validationErrors = validate(editStock);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }
        setIsSubmitting(true);
        try {
            await handleUpdate(editStock);
            resetDirty();
            setEditStock(null);
            setErrors({});
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setEditStock(null);
        setErrors({});
        resetDirty();
    };

    if (!editStock) {
        return null;
    }

    return (
        <Card className="mb-6">
            <CardHeader>
                <CardTitle>Modifier un stock</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <Label htmlFor="edit-symbol">Symbole boursier</Label>
                    <Input
                        id="edit-symbol"
                        value={editStock.stock_symbol}
                        readOnly
                        className="bg-muted"
                    />
                </div>
                <div>
                    <Label htmlFor="edit-name">Nom de la société</Label>
                    <Input
                        id="edit-name"
                        value={editStock.company_name}
                        onChange={(e) => handleInputChange('company_name', e.target.value)}
                        className={errors.company_name ? "border-destructive" : ""}
                    />
                    {errors.company_name && (
                        <p className="text-sm text-destructive mt-1">{errors.company_name}</p>
                    )}
                </div>
            </CardContent>
            <CardFooter className="flex flex-col sm:flex-row gap-2 justify-end">
                <Button
                    variant="default"
                    disabled={isSubmitting || Object.keys(validate(editStock)).length > 0}
                    onClick={handleSubmit}
                >
                    {isSubmitting ? (
                        <>
                            <Spinner className="h-4 w-4 mr-2" />
                            Enregistrement...
                        </>
                    ) : (
                        "Enregistrer"
                    )}
                </Button>
                <Button
                    variant="outline"
                    onClick={handleClose}
                >
                    Annuler
                </Button>
            </CardFooter>
            <DirtyStateDialog open={showDialog} onConfirm={confirmNavigation} onCancel={cancelNavigation} />
        </Card>
    );
};

export default EditStock;
